import { FC } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  message: string
  itemName?: string
  warningMessage?: string
  confirmText?: string
  cancelText?: string
  onConfirm: () => void
  onCancel: () => void
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'
  isLoading?: boolean
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  itemName,
  warningMessage = 'Esta acción no se puede deshacer.',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  confirmColor = 'error',
  isLoading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: '#ef4444',
          fontWeight: 600,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 28 }} />
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description" sx={{ color: '#374151', fontSize: '1rem' }}>
          {message}
          {itemName && (
            <>
              {' '}
              <strong>"{itemName}"</strong>?
            </>
          )}
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#fef2f2',
              borderRadius: 1,
              border: '1px solid #fecaca',
            }}
          >
            <Typography variant="body2" sx={{ color: '#991b1b' }}>
              {warningMessage}
            </Typography>
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            ...(confirmColor === 'error' && {
              backgroundColor: '#ef4444',
              '&:hover': {
                backgroundColor: '#dc2626',
              },
            }),
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
